/**
 * @file: useCreateChildOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { orderFormFieldOf } from '@euler/app/flows/order/functions/orderFormFieldOf';
import { useCreateChildOrderRequest } from '@euler/app/flows/order/functions/useCreateChildOrderRequest';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useAlert } from '@euler/functions/ux/useAlert';
import { markRequestViolation } from '@euler/lib/form/markRequestViolation';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag, sleep } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import invariant from 'tiny-invariant';
import { useOrderFlowContext } from '../../../OrderFlowContext';

const kSimulate = safeMarkDevFlag(false);
const kSimulateError = safeMarkDevFlag(false);
const kFake = safeMarkDevFlag(false);

export const useCreateChildOrder = () => {
  const alert = useAlert();
  const trackAppReview = useAppReviewTracking();
  const appLoading = useAppLoading();
  const { orderService } = useServiceFactory();
  const { event$ } = useOrderContext();
  const {
    mainOrderNo,
    formController: { form },
    onCreated,
    finish,
  } = useOrderFlowContext();

  const getCreateOrderRequest = useCreateChildOrderRequest();

  return usePersistFn(async () => {
    try {
      invariant(mainOrderNo != null, 'mainOrderNo is required');
      const request = getCreateOrderRequest();
      __DEV__ && console.log(JSON.stringify(request, null, 2));

      appLoading.show();

      if (kFake) {
        await sleep(3000);
      } else {
        const { order, task, ...res } = await orderService.createChildOrder(
          mainOrderNo,
          request,
          {
            __fake__: kSimulate,
            __simulatefailure__: kSimulateError,
          },
        );

        __DEV__ && console.log('place child order success: ', order);

        finish({ simulate: kSimulate });

        if (res.mainOrderNo !== mainOrderNo) {
          event$.next({
            type: 'created',
            orderNo: res.mainOrderNo,
            taskNo: res.mainTaskNo,
            isMainOrder: false,
          });
        }

        event$.next({
          type: 'created',
          isMainOrder: false,
          mainOrderNo: res.mainOrderNo,
          mainTaskNo: res.mainTaskNo,
          orderNo: order.orderNo,
          taskNo: task.taskNo,
        });

        onCreated?.(order, task);

        trackAppReview();
      }
    } catch (e) {
      console.error(e);
      // The server's sentence is localized and names what was wrong.
      markRequestViolation(form, e, orderFormFieldOf);
      void alert({
        title: '出错了',
        message: (e as Error).message,
      });
    } finally {
      appLoading.hide();
    }
  });
};
